import { CATEGORIES } from '@kijiji/category'
import { useTranslation } from 'next-i18next'
import { type FC } from 'react'
import { useTheme } from 'styled-components'

import { BrandedTile } from '@/components/homepage/popular-categories/BrandedTile'
import { handlePopularCategoryTracking } from '@/components/homepage/popular-categories/domain'
import { PopularCategoriesHeader } from '@/components/homepage/popular-categories/PopularCategoriesHeader'
import { popularOnBuySellStaticData } from '@/components/homepage/popular-categories/popularCategoriesStaticData'
import { GalleryCard } from '@/components/shared/gallery/GalleryCard'
import { GalleryHeaderWrapper } from '@/components/shared/gallery/GalleryHeaderWrapper'
import { GalleryScrollableContainer } from '@/components/shared/gallery/GalleryScrollableContainer'
import { GalleryWrapper } from '@/components/shared/gallery/GalleryWrapper'
import { type BrandedTileData } from '@/types/customCampaigns'

export type PopularOnBuyAndSellProps = {
  /**
   * Branded tile
   */
  campaign?: BrandedTileData
  /**
   * Defines if user is on a region location
   */
  isRegionLocation: boolean
  /**
   * Defines the location name user is currently in
   */
  locationName?: string | null
  /**
   * Gets the SEO url based on a categoryId
   */
  getSeoUrl: (categoryId: number, index?: number) => string
}

export const PopularOnBuyAndSell: FC<PopularOnBuyAndSellProps> = ({
  campaign,
  getSeoUrl,
  isRegionLocation,
  locationName,
}) => {
  const { t } = useTranslation('home')
  const { spacing } = useTheme()

  const categorySectionId = CATEGORIES.BUY_AND_SELL_CATEGORY_ID
  const hasBrandedTile = campaign && campaign.hpCategory === categorySectionId

  return (
    <GalleryWrapper
      data-testid="homepage-popular-buy-sell"
      flexDirection="column"
      gap={spacing.default}
    >
      <GalleryHeaderWrapper>
        <PopularCategoriesHeader
          browseAllCategoryId={categorySectionId}
          getSeoUrl={getSeoUrl}
          isRegionLocation={isRegionLocation}
          locationName={locationName}
          sectionKey="buy_sell"
        />
      </GalleryHeaderWrapper>

      <GalleryScrollableContainer>
        {/* Branded Tile */}
        {hasBrandedTile && <BrandedTile campaign={campaign} />}

        {popularOnBuySellStaticData.map((item, index) => {
          const seoUrl = getSeoUrl(item.id, item.index)
          const position = index + 1

          if (hasBrandedTile && popularOnBuySellStaticData.length === position) {
            return null
          }

          return (
            <GalleryCard
              centralizedTitle={t(`popular_categories.buy_sell.${item.translationKey}`)}
              href={seoUrl}
              image={{ alt: '', src: item.assetSrc }}
              index={position}
              key={`hp-popular-buy_sell-${position}`}
              onClick={() => handlePopularCategoryTracking(item.id)}
              useLegacyLgDesktopBreakpoint
            />
          )
        })}
      </GalleryScrollableContainer>
    </GalleryWrapper>
  )
}
